/* eslint-disable import/no-extraneous-dependencies */
import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import AceEditor from 'react-ace';
import InputTextLine from '../../../juristec-ui/core/InputTextLine';
import ActionsGroup from '../../../juristec-ui/core/ActionsGroup';
import Button from '../../../juristec-ui/core/Button';
import Select from '../../../juristec-ui/core/Select';
import InputNumberLine from '../../../juristec-ui/core/InputNumberLine';

// Import AceEditor modes and themes
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-monokai';

import {
  Database,
  DesktopUpload,
  File,
  GoogleDriveNew,
  Memory,
  OneDrive,
  Cloud,
  ExpandMoreWide,
  BennerSimpleGray,
  // LegalOne,
} from '../../../juristec-ui/icons';

import { printSize } from '../../../juristec-ui/utils/functions/lab';

import {
  verifyGoogleDriveSheetLink, verifySqlCode, verifyInput, verifyOneDriveSheetLink, verifyFileName,
} from '../../../juristec-ui/utils/validators/inputTextValidators';

import { verifyFileSheetType, verifyFileSize } from '../../../juristec-ui/utils/validators/fileValidators';

import {
  MainContainer,
  FormContainer,
  UploadArea,
  UploadBorder,
  UploadOpts,
  FileInfoContainer,
  InputContainer,
  FileInfo,
  InfoLabels,
  TabGroup,
  TabButton,
  InputRow,
  OptionGroup,
  Option,
} from './styled/FileUpload.styled';

const fileUploadDefaultPropTourContext = { tourOpen: false, nextStep: () => {} };
const fileUploadDefaultPropUploadFileByLink = () => {};
const fileUploadDefaultPropUploadBySql = () => {};
const fileUploadDefaultPropTestSqlConnection = () => {};
const fileUploadDefaultPropUploadBennerReport = () => {};

/**
* A modal for uploading files from desktop or google drive link
*/
const FileUpload = ({
  availableSize = 0,
  uploadFile,
  uploadFileByLink = fileUploadDefaultPropUploadFileByLink,
  uploadBySql = fileUploadDefaultPropUploadBySql,
  testSqlConnection = fileUploadDefaultPropTestSqlConnection,
  uploadBennerReport = fileUploadDefaultPropUploadBennerReport,
  updateMode = false,
  updatingFileName = '',
  hide,
  tourContext = fileUploadDefaultPropTourContext,
}) => {
  const [tab, setTab] = useState('desktop');
  const [dragging, setDragging] = useState(false);
  const [inputLink, setInputLink] = useState({ url: '', errorMsg: '' });
  const [inputLinkFilename, setInputLinkFilename] = useState({ value: '', errorMsg: '' });
  const [inputLinkStartLine, setInputLinkStartLine] = useState({ value: 1, errorMsg: '' });
  const [inputLinkConnector, setInputLinkConnector] = useState('GoogleDrive');

  const [selectedFile, setSelectedFile] = useState({ file: null, errorMsg: '' });

  const [inputDBType, setInputDBType] = useState({ label: 'PostgreSQL', value: 'pgsql', dialect: 'Postgresql' });
  const [inputDBHost, setInputDBHost] = useState({ value: '', errorMsg: '' });
  const [inputDBPort, setInputDBPort] = useState({ value: '', errorMsg: '' });
  const [inputDBName, setInputDBName] = useState({ value: '', errorMsg: '' });
  const [inputDBFilename, setInputDBFilename] = useState({ value: '', errorMsg: '' });
  const [inputDBUser, setInputDBUser] = useState({ value: '', errorMsg: '' });
  const [inputDBPass, setInputDBPass] = useState({ value: '', errorMsg: '' });
  const [inputDBCode, setInputDBCode] = useState({ value: '', errorMsg: '' });

  const [inputBennerURL, setInputBennerURL] = useState({ value: '', errorMsg: '' });
  const [inputBennerFilename, setInputBennerFilename] = useState({ value: '', errorMsg: '' });
  const [inputBennerUsername, setInputBennerUsername] = useState({ value: '', errorMsg: '' });
  const [inputBennerPassword, setInputBennerPassword] = useState({ value: '', errorMsg: '' });
  const [inputBennerStartLine, setInputBennerStartLine] = useState({ value: 1, errorMsg: '' });

  const fileInputRef = useRef(null);

  const dragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const dragEnter = (e) => {
    e.preventDefault();
  };

  const dragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleFile = (files) => {
    const errorMsg = verifyFileSheetType(files[0]) || verifyFileSize(files[0], availableSize);
    setSelectedFile({
      file: errorMsg.length === 0 ? files[0] : null,
      errorMsg,
    });
    if (tourContext.tourOpen && errorMsg.length === 0) tourContext.nextStep();
  };

  const fileDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files;
    if (file.length) {
      handleFile(file);
    }
  };

  const handleFileLink = (e) => {
    const val = e.target.value.trim();
    setInputLink({
      url: val,
      errorMsg: inputLinkConnector === 'GoogleDrive' ? verifyGoogleDriveSheetLink(val) : verifyOneDriveSheetLink(val),
    });
  };

  const handleConnectorLink = (connector) => {
    setInputLink((o) => ({
      ...o,
      errorMsg: connector === 'GoogleDrive' ? verifyGoogleDriveSheetLink(o.url) : verifyOneDriveSheetLink(o.url),
    }));
    setInputLinkConnector(connector);
  };

  const handleLinkFilename = (e) => {
    const val = e.target.value.trim();
    setInputLinkFilename({ value: val, errorMsg: verifyFileName(val) });
  };

  const handleLinkStartLine = (value) => {
    if (value.length === 0) {
      setInputLinkStartLine({ value, errorMsg: 'Este campo não pode estar vazio' });
      return;
    }
    if (value === '0') {
      // eslint-disable-next-line no-param-reassign
      value = 1;
      setInputLinkStartLine({ value: 1, errorMsg: '' });
    } else if (/^[0-9]+$/.test(value)) {
      setInputLinkStartLine({ value, errorMsg: '' });
    }
  };

  const fileSelected = () => {
    if (fileInputRef.current.files.length) {
      handleFile(fileInputRef.current.files);
    }
  };

  const handleDBType = (selection) => {
    setInputDBType(selection);
  };

  const handleDBHost = (e) => {
    const val = e.target.value.trim();
    setInputDBHost({ value: val, errorMsg: verifyInput(val, true) });
  };

  const handleDBPort = (e) => {
    const val = e.target.value.trim();
    setInputDBPort({ value: val, errorMsg: verifyInput(val, true) });
  };

  const handleDBName = (e) => {
    const val = e.target.value.trim();
    setInputDBName({ value: val, errorMsg: verifyInput(val, true) });
  };

  const handleDBFilename = (e) => {
    const val = e.target.value.trim();
    setInputDBFilename({ value: val, errorMsg: verifyFileName(val) });
  };

  const handleDBUser = (e) => {
    const val = e.target.value.trim();
    setInputDBUser({ value: val, errorMsg: verifyInput(val, true) });
  };

  const handleDBPass = (e) => {
    const val = e.target.value.trim();
    setInputDBPass({ value: val, errorMsg: verifyInput(val, true) });
  };

  const handleDBCode = (e) => {
    setInputDBCode({ value: e, errorMsg: verifySqlCode(inputDBType.dialect, e.trim()) });
  };

  const handleBennerURL = (e) => {
    const val = e.target.value.trim();
    setInputBennerURL({ value: val, errorMsg: verifyInput(val, true) });
  };

  const handleBennerFilename = (e) => {
    const val = e.target.value.trim();
    setInputBennerFilename({ value: val, errorMsg: verifyFileName(val) });
  };

  const handleBennerUsername = (e) => {
    const val = e.target.value.trim();
    setInputBennerUsername({ value: val, errorMsg: verifyInput(val, true) });
  };

  const handleBennerPassword = (e) => {
    const val = e.target.value.trim();
    setInputBennerPassword({ value: val, errorMsg: verifyInput(val, true) });
  };

  const handleBennerStartLine = (value) => {
    if (value.length === 0) {
      setInputBennerStartLine({ value, errorMsg: 'Este campo não pode estar vazio' });
      return;
    }
    if (value === '0') {
      // eslint-disable-next-line no-param-reassign
      value = 1;
      setInputBennerStartLine({ value: 1, errorMsg: '' });
    } else if (/^[0-9]+$/.test(value)) {
      setInputBennerStartLine({ value, errorMsg: '' });
    }
  };

  const toggleUploadBtn = () => {
    switch (tab) {
      case 'drive':
        return (
          inputLink.url.length > 0 && inputLink.errorMsg.length === 0
          && inputLinkFilename.value.length > 0 && inputLinkFilename.errorMsg.length === 0
          && inputLinkStartLine.value > 0 && inputLinkStartLine.errorMsg.length === 0
        );
      case 'desktop':
        return selectedFile.file && selectedFile.errorMsg.length === 0;
      case 'sql':
        return (
          inputDBCode.value.trim().length > 0 && inputDBCode.errorMsg.length === 0
          && inputDBHost.value.length > 0 && inputDBHost.errorMsg.length === 0
          && inputDBPort.value.length > 0 && inputDBPort.errorMsg.length === 0
          && inputDBName.value.length > 0 && inputDBName.errorMsg.length === 0
          && inputDBFilename.value.length > 0 && inputDBFilename.errorMsg.length === 0
          && inputDBUser.value.length > 0 && inputDBUser.errorMsg.length === 0
          && inputDBPass.value.length > 0 && inputDBPass.errorMsg.length === 0
          && inputDBCode.value.length > 0 && inputDBCode.errorMsg.length === 0
        );
      case 'benner':
        return (
          inputBennerURL.value.length > 0 && inputBennerURL.errorMsg.length === 0
          && inputBennerFilename.value.length > 0 && inputBennerFilename.errorMsg.length === 0
          && inputBennerUsername.value.length > 0 && inputBennerUsername.errorMsg.length === 0
          && inputBennerPassword.value.length > 0 && inputBennerPassword.errorMsg.length === 0
        );
      default:
        return false;
    }
  };

  const handleUpload = () => {
    switch (tab) {
      case 'drive':
        uploadFileByLink(
          inputLink.url,
          inputLinkFilename.value,
          inputLinkStartLine.value - 1,
          inputLinkConnector,
        );
        break;
      case 'desktop':
        uploadFile(selectedFile.file);
        break;
      case 'sql':
        uploadBySql(
          {
            filename: inputDBFilename.value,
            dialect: inputDBType.value,
            username: inputDBUser.value,
            password: inputDBPass.value,
            host: inputDBHost.value,
            port: inputDBPort.value,
            dbname: inputDBName.value,
            query: inputDBCode.value.trim(),
          },
        );
        break;
      case 'benner':
        uploadBennerReport(
          inputBennerFilename.value,
          inputBennerURL.value,
          inputBennerUsername.value,
          inputBennerPassword.value,
          inputBennerStartLine.value,
        );
        break;
      default:
        break;
    }
  };

  return (
    <>
      <MainContainer>
        {!updateMode && (
          <TabGroup>
            <TabButton active={tab === 'desktop'} onClick={() => setTab('desktop')}>
              <Memory />
              <span>Meu dispositivo</span>
              <div className="icon-detail">
                <ExpandMoreWide />
              </div>
            </TabButton>
            <TabButton active={tab === 'drive'} onClick={() => setTab('drive')}>
              <Cloud />
              <span>Drive (Nuvem)</span>
              <div className="icon-detail">
                <ExpandMoreWide />
              </div>
            </TabButton>
            <TabButton active={tab === 'sql'} onClick={() => setTab('sql')}>
              <Database />
              <span>Base SQL</span>
              <div className="icon-detail">
                <ExpandMoreWide />
              </div>
            </TabButton>
            {process.env.REACT_APP_FIREBASE_PROJECT_LABEL === 'benner-metrics' && (
              <TabButton active={tab === 'benner'} onClick={() => setTab('benner')}>
                <BennerSimpleGray />
                <span>Relatório Benner</span>
                <div className="icon-detail">
                  <ExpandMoreWide />
                </div>
              </TabButton>
            )}
            {/*
            TAB PARA RELATÓRIOS DO LEGAL ONE SUSPENSA POR ENQUANTO.
            SOMENTE QUANDO O DATA EXTRACTOR FUNCIONAR
            */}
            {/*
            {process.env.REACT_APP_FIREBASE_PROJECT_LABEL === 'legalone-analytics' && (
              <TabButton active={tab === 'legalOne'} onClick={() => setTab('legalOne')}>
                <LegalOne />
                Relatório LegalOne
              </TabButton>
            )}
            */}
          </TabGroup>
        )}
        <FormContainer>
          {tab === 'desktop' && (
            <UploadArea
              onDragOver={dragOver}
              onDragEnter={dragEnter}
              onDragLeave={dragLeave}
              onDrop={fileDrop}
              dragging={dragging}
            >
              <UploadBorder>
                <span>
                  Arraste um arquivo
                  {' '}
                  <b>.xlsx</b>
                  {' '}
                  ou
                  {' '}
                  <b>.csv</b>
                </span>
                <span><b>ou</b></span>
                <UploadOpts>
                  <input
                    ref={fileInputRef}
                    className="file-input"
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    style={{ display: 'none' }}
                    onChange={fileSelected}
                  />
                  <Button
                    size="small"
                    shape="rounded"
                    onClick={() => fileInputRef.current.click()}
                    className="modal_upload_file"
                  >
                    <DesktopUpload />
                    Procure no dispositivo...
                  </Button>
                </UploadOpts>
                <FileInfoContainer>
                  <>
                    {selectedFile.errorMsg.length > 0 ? (
                      <span className="errorMsg">{selectedFile.errorMsg}</span>
                    ) : (
                      selectedFile.file && (
                        <FileInfo>
                          <File />
                          <span>{selectedFile.file.name}</span>
                        </FileInfo>
                      )
                    )}
                  </>
                </FileInfoContainer>
              </UploadBorder>
            </UploadArea>
          )}
          {tab === 'drive' && (
            <InputContainer>
              <span>
                Informe os campos abaixo para enviar um arquivo compartilhado no drive. Obs: o
                arquivo deve estar compartilhado com acesso público.
              </span>
              <OptionGroup>
                <Option
                  onClick={() => handleConnectorLink('GoogleDrive')}
                  active={inputLinkConnector === 'GoogleDrive'}
                >
                  <GoogleDriveNew />
                  Google Drive
                </Option>
                <Option
                  onClick={() => handleConnectorLink('OneDrive')}
                  active={inputLinkConnector === 'OneDrive'}
                >
                  <OneDrive />
                  One Drive
                </Option>
              </OptionGroup>
              <InputTextLine
                label="Nome do arquivo resultante"
                value={inputLinkFilename.value}
                onChange={handleLinkFilename}
                error={inputLinkFilename.errorMsg.length > 0}
                errorMessage={inputLinkFilename.errorMsg}
                autocomplete="new-password"
              />
              <InputTextLine
                label="Link de compartilhamento público"
                // placeholder="Link do arquivo"
                error={inputLink.errorMsg.length > 0}
                errorMessage={inputLink.errorMsg}
                value={inputLink.url}
                onChange={handleFileLink}
                autocomplete="new-password"
              />
              <InputRow style={{ alignItems: 'center' }}>
                <span className="extra-label">Iniciar a partir da linha</span>
                <InputNumberLine
                  // label="Iniciar a partir da linha"
                  value={inputLinkStartLine.value}
                  onChange={(e) => handleLinkStartLine(e.target.value)}
                  setValue={handleLinkStartLine}
                  min={1}
                  error={inputLinkStartLine.errorMsg.length > 0}
                  errorMessage={inputLinkStartLine.errorMsg}
                />
              </InputRow>
            </InputContainer>
          )}
          {tab === 'sql' && (
            <InputContainer style={{ height: '100%' }}>
              <span>
                Informe os campos abaixo para gerar um arquivo a partir de uma base SQL.
                Recomendamos que o usuário de acesso possua apenas permissão de leitura.
              </span>
              <InputRow>
                <div>
                  <Select
                    selectLabel="Tipo"
                    placeholder="Selecione"
                    options={[
                      {
                        label: 'PostgreSQL', value: 'pgsql', id: 'PGSQL', dialect: 'Postgresql',
                      },
                      {
                        label: 'MySQL', value: 'mysql', id: 'MYSQL', dialect: 'MySQL',
                      },
                      {
                        label: 'Oracle', value: 'oracle', id: 'ORACLE', dialect: 'Oracle',
                      },
                      {
                        label: 'SQL Server', value: 'sqlserver', id: 'SQLSERVER', dialect: 'TransactSQL',
                      },
                    ]}
                    value={inputDBType}
                    onChange={handleDBType}
                    fullWidth
                    atModal
                  />
                </div>
                <InputTextLine
                  label="Host"
                  value={inputDBHost.value}
                  onChange={handleDBHost}
                  error={inputDBHost.errorMsg.length > 0}
                  errorMessage={inputDBHost.errorMsg}
                  autocomplete="new-password"
                />
                <InputTextLine
                  label="Porta"
                  wrapperStyle={{ width: '20%' }}
                  value={inputDBPort.value}
                  onChange={handleDBPort}
                  error={inputDBPort.errorMsg.length > 0}
                  errorMessage={inputDBPort.errorMsg}
                  autocomplete="new-password"
                />
              </InputRow>
              <InputRow>
                <InputTextLine
                  label="Nome da base de dados"
                  value={inputDBName.value}
                  onChange={handleDBName}
                  error={inputDBName.errorMsg.length > 0}
                  errorMessage={inputDBName.errorMsg}
                  autocomplete="new-password"
                />
                <InputTextLine
                  label="Nome do arquivo resultante"
                  value={inputDBFilename.value}
                  onChange={handleDBFilename}
                  error={inputDBFilename.errorMsg.length > 0}
                  errorMessage={inputDBFilename.errorMsg}
                  autocomplete="new-password"
                />
              </InputRow>
              <InputRow>
                <InputTextLine
                  label="Usuário da base"
                  value={inputDBUser.value}
                  onChange={handleDBUser}
                  error={inputDBUser.errorMsg.length > 0}
                  errorMessage={inputDBUser.errorMsg}
                  autocomplete="new-password"
                />
                <InputTextLine
                  label="Senha da base"
                  type="password"
                  value={inputDBPass.value}
                  onChange={handleDBPass}
                  error={inputDBPass.errorMsg.length > 0}
                  errorMessage={inputDBPass.errorMsg}
                  autocomplete="new-password"
                />
                <Button
                  style={{ margin: '3px' }}
                  onClick={
                    () => testSqlConnection({
                      dialect: inputDBType.value,
                      username: inputDBUser.value,
                      password: inputDBPass.value,
                      host: inputDBHost.value,
                      port: inputDBPort.value,
                      dbname: inputDBName.value,
                    })
                  }
                  size="small"
                  disabled={
                    !(
                      inputDBHost.value.length > 0
                      && inputDBHost.errorMsg.length === 0
                      && inputDBPort.value.length > 0
                      && inputDBPort.errorMsg.length === 0
                      && inputDBName.value.length > 0
                      && inputDBName.errorMsg.length === 0
                      && inputDBUser.value.length > 0
                      && inputDBUser.errorMsg.length === 0
                      && inputDBPass.value.length > 0
                      && inputDBPass.errorMsg.length === 0
                    )
                  }
                  className="modal_upload_file_advance"
                >
                  Testar Conexão
                </Button>
              </InputRow>
              <InputRow style={{ height: '100%' }}>
                <AceEditor
                  mode="sql"
                  theme="monokai"
                  fontSize={14}
                  lineHeight={19}
                  scrollMargin={[0, 0]}
                  enableLiveAutocompletion
                  enableBasicAutocompletion
                  enableSnippets
                  tabSize={4}
                  showPrintMargin
                  showGutter
                  highlightActiveLine
                  value={inputDBCode.value}
                  style={{ width: '100%', height: '100%' }}
                  onChange={handleDBCode}
                  name="CodeEditor"
                  editorProps={{ $blockScrolling: true }}
                  placeholder="SELECT ... FROM ... WHERE ..."
                  setOptions={{
                    enableBasicAutocompletion: true,
                    enableLiveAutocompletion: true,
                    enableSnippets: true,
                    enableMobileMenu: true,
                    showLineNumbers: true,
                  }}
                />
              </InputRow>
            </InputContainer>
          )}
          {process.env.REACT_APP_FIREBASE_PROJECT_LABEL === 'benner-metrics' && tab === 'benner' && (
            <InputContainer style={{ height: '100%' }}>
              <span>Informe os campos abaixo para gerar um relatório do ERP Benner.</span>
              <InputRow>
                <InputTextLine
                  label="URL Benner"
                  value={inputBennerURL.value}
                  onChange={handleBennerURL}
                  error={inputBennerURL.errorMsg.length > 0}
                  errorMessage={inputBennerURL.errorMsg}
                  autocomplete="new-password"
                />
                <InputTextLine
                  label="Nome do arquivo resultante"
                  value={inputBennerFilename.value}
                  onChange={handleBennerFilename}
                  error={inputBennerFilename.errorMsg.length > 0}
                  errorMessage={inputBennerFilename.errorMsg}
                  autocomplete="new-password"
                />
              </InputRow>
              <InputRow>
                <InputTextLine
                  label="Usuário Benner"
                  value={inputBennerUsername.value}
                  onChange={handleBennerUsername}
                  error={inputBennerUsername.errorMsg.length > 0}
                  errorMessage={inputBennerUsername.errorMsg}
                  autocomplete="new-password"
                />
                <InputTextLine
                  label="Senha Benner"
                  type="password"
                  value={inputBennerPassword.value}
                  onChange={handleBennerPassword}
                  error={inputBennerPassword.errorMsg.length > 0}
                  errorMessage={inputBennerPassword.errorMsg}
                  autocomplete="new-password"
                />
              </InputRow>
              <InputRow style={{ alignItems: 'center' }}>
                <span className="extra-label">Iniciar a partir da linha</span>
                <InputNumberLine
                  // label="Iniciar a partir da linha"
                  value={inputBennerStartLine.value}
                  onChange={(e) => handleBennerStartLine(e.target.value)}
                  setValue={handleBennerStartLine}
                  min={1}
                  error={inputBennerStartLine.errorMsg.length > 0}
                  errorMessage={inputBennerStartLine.errorMsg}
                />
              </InputRow>
            </InputContainer>
          )}
        </FormContainer>
      </MainContainer>
      <InfoLabels>
        {updateMode && <span>{`Atualizando: ${updatingFileName}`}</span>}
        <span>{`Espaço livre: ${printSize(availableSize)}`}</span>
      </InfoLabels>
      <ActionsGroup>
        <Button style={{ margin: '5px' }} onClick={hide} variant="outlined" size="large">
          Cancelar
        </Button>
        <Button
          style={{ margin: '5px' }}
          onClick={() => handleUpload()}
          size="large"
          disabled={!toggleUploadBtn()}
          className="modal_upload_file_advance"
        >
          Adicionar
        </Button>
      </ActionsGroup>
    </>
  );
};

FileUpload.propTypes = {
  /**
    * The maximum size available for upload
    */
  availableSize: PropTypes.number,
  /**
    * A function to handle a file upload from desktop
    */
  uploadFile: PropTypes.func.isRequired,
  /**
    * A function to handle a file upload from Google Drive
    */
  uploadFileByLink: PropTypes.func,
  /**
    * A function to handle a file upload from sql database
    */
  uploadBySql: PropTypes.func,
  /**
    * A function test the connection to a sql database
    */
  testSqlConnection: PropTypes.func,
  /**
    * A function to create a Benner Report
    */
  uploadBennerReport: PropTypes.func,
  /**
    * A boolean to toggle between add or update mode
    */
  updateMode: PropTypes.bool,
  /**
    * The name of the file to be updated. Used only when 'updateMode' is true
    */
  updatingFileName: PropTypes.string,
  /**
    * A function that closes the modal
    */
  hide: PropTypes.func.isRequired,

  tourContext: PropTypes.shape({
    tourOpen: PropTypes.bool,
    nextStep: PropTypes.func,
  }),
};

export default FileUpload;
